import { useEffect, useMemo, useState } from 'react';
import mapDataToCards, { CardConfig, getFabricText } from '../../../../utils/artDetailsUtils';
import { getBackendApiUrl } from '../../../../config';
import { getAdminId } from '../../../../api/network';
import { PRODUCT_FIELD_RULES, ProductRules } from './useCardConfigRules';
import { getText } from '../filterOptionsDropdown';

const HIDDEN_KEYS = ['artNotes', 'sewFileEmbroideryNotes', 'aspectRatio', 'uploadId'];
const server = 'https://www.stahls.com/stahls/13/artworkupload/mapjobdatadetails?mapName=cart'

type Label = { val: string | number | boolean, grp: number, lbl: string }
type LabelMap = { [key: string]: Label }

interface UnifiedData {
  [key: string]: string | number | boolean;
}

async function mapWizardData(wizardData: any) {
  const pathAndSearch = ((x: URL) => x.pathname + x.search)(new URL(server))
  const url = new URL(pathAndSearch, getBackendApiUrl())
  const result = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(wizardData)
  })

  const mapLabels = (await result.json()) as LabelMap

  const groupedLabels = new Map<number, Label[]>()
  for (const [, label] of Object.entries(mapLabels)) {
    const grpId = label.grp
    if (!groupedLabels.has(grpId)) {
      groupedLabels.set(grpId, [label])
    } else {
      groupedLabels.get(grpId)?.push(label)
    }
  }

  return {
    groupedLabels,
    mapLabels,
  };
}

const internalEmbroideryNoteKeys = [
  'sewFileInternalEmbroideryNotes',
  'sewFileInternalEmbroideryNotesBy',
  'sewFileInternalEmbroideryNotesDate',
];

const convertHtmlEntities = (text: string): string => {
  return text
    .replace(/&reg;/g, '®')
    .replace(/&copy;/g, '©')
    .replace(/&trade;/g, '™')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
};

const isValidValue = (value: any): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  if (typeof value === 'number') {
    return !isNaN(value);
  }

  return true;
};

const getAllDefinedFields = (cardConfigs: CardConfig[]): Set<string> => {
  const definedFields = new Set<string>();
  cardConfigs.forEach(card => {
    card.properties.forEach(prop => {
      //@ts-ignore
      definedFields.add(prop.key);
    });
  });
  return definedFields;
};

const createUnifiedData = (
  wizardDocData: any,
  wizardProps: any,
  mapLabels: LabelMap | null,
  mappedCards: CardConfig[],
  artId: string | null
): UnifiedData => {
  const result: UnifiedData = {};
  const definedFields = getAllDefinedFields(mappedCards);
  const SPECIAL_KEYS = ['serviceType', 'productType'];

  const processValue = (key: string, value: any, isFromMapLabels = false): any => {
    if (key === 'garmentFabric') {
      const fabricText = getFabricText(value as string);
      return fabricText || value;
    }

    if (SPECIAL_KEYS.includes(key) && key !== 'garmentFabric') {
      return isFromMapLabels
        ? convertHtmlEntities(value as string)
        : getText[value as keyof typeof getText];
    }
    return value;
  };

  const getValidColorMaterialKeys = (mapLabels: LabelMap, wizardData: any): string[] => {
    const colorMaterialKeys: string[] = [];
    const mapLabelKeys = Object.keys(mapLabels);
    const isSameMaterial = wizardData?.sameMaterial === 'yes';

    for (let i = 1; i <= 10; i++) {
      const materialKey = `color${i}Material`;
      const materialColorKey = `color${i}MaterialColor`;

      // sameMaterial="yes"
      if (isSameMaterial) {
        const hasInMapLabels = mapLabelKeys.includes(materialColorKey);
        const hasInWizardData = wizardData[materialColorKey];

        if ((hasInMapLabels || hasInWizardData) && wizardData?.colorAllMaterial) {
          colorMaterialKeys.push(materialKey, materialColorKey);
        }
      } else {
        if (mapLabelKeys.includes(materialKey) &&
          mapLabelKeys.includes(materialColorKey) &&
          wizardData[materialKey] &&
          wizardData[materialColorKey]) {
          colorMaterialKeys.push(materialKey, materialColorKey);
        }
      }
    }

    return colorMaterialKeys;
  };

  const calculateActualColorCount = (validColorKeys: string[]): number => {
    // Count unique color numbers
    const colorNumbers = new Set<number>();
    validColorKeys.forEach(key => {
      const match = key.match(/color(\d+)/);
      if (match) {
        colorNumbers.add(parseInt(match[1]));
      }
    });
    return colorNumbers.size;
  };

  const processEntries = (
    entries: [string, any][],
    shouldCheckDefinedFields = true,
    isFromMapLabels = false
  ) => {
    entries.forEach(([key, value]) => {
      if (
        isValidValue(value) &&
        (!shouldCheckDefinedFields || definedFields.has(key)) &&
        !HIDDEN_KEYS.includes(key) &&
        !(key in result)
      ) {
        result[key] = processValue(key, value, isFromMapLabels);
      }
    });
  };

  if (artId && isValidValue(artId)) {
    result['artId'] = artId;
  }

  if (wizardDocData?.serviceType === 'CustomProduct') {
    result['productType'] = 'Uploaded File';
  }

  const validColorMaterialKeys = mapLabels && wizardDocData ?
    getValidColorMaterialKeys(mapLabels, wizardDocData) : [];

  // 1. Process mapLabels with special handling for color materials
  if (mapLabels) {
    const mapLabelEntries = Object.entries(mapLabels)
      .filter(([key, item]) => {
        if (!item || typeof item !== 'object' || !('val' in item) || !isValidValue(item.val)) {
          return false;
        }

        if (key.match(/color\d+(Material|MaterialColor)/)) {
          return validColorMaterialKeys.includes(key);
        }

        return true;
      })
      .map(([key, item]) => {
        let value = item.val;

        if (key === 'colorCount') {
          value = calculateActualColorCount(validColorMaterialKeys);
        }

        return [key, value] as [string, any];
      })
      .filter(([, val]) =>
        typeof val === 'string' ||
        typeof val === 'number' ||
        typeof val === 'boolean'
      );

    processEntries(mapLabelEntries, false, true);
  }

  // 2. Process wizardProps
  if (wizardProps) {
    const filteredWizardProps = Object.entries(wizardProps)
      .filter(([key]) => {
        if (key.match(/color\d+(Material|MaterialColor)/)) {
          return false;
        }
        if (key === 'colorCount') {
          return false;
        }
        return true;
      });

    processEntries(filteredWizardProps, true, false);
  }

  // 3. Process wizardDocData
  if (wizardDocData) {
    const filteredWizardDocData = Object.entries(wizardDocData)
      .filter(([key]) => {
        if (key.match(/color\d+(Material|MaterialColor)/)) {
          return false;
        }
        if (key === 'colorCount') {
          return false;
        }
        return true;
      });

    processEntries(filteredWizardDocData, true, false);
  }

  // 4. Process extendedData
  if (wizardDocData?.extendedData) {
    processEntries(Object.entries(wizardDocData.extendedData), false, false);
  }

  return result;
};

const findFieldInCards = (fieldKey: string, cardConfigs: CardConfig[], isFromMapLabels: boolean = false): string => {
  if (fieldKey === 'artId') {
    return 'ART DETAILS';
  }

  if (!isFromMapLabels) {
    for (const card of cardConfigs) {
      if (card.properties.some(prop => prop.key === fieldKey)) {
        return card.title;
      }
    }
    // PRODUCT DETAILS by default if there's not a match
    return 'PRODUCT DETAILS';
  }

  const lowerKey = fieldKey.toLowerCase();

  if (fieldKey === 'colorAllStitchType' ||
    fieldKey === 'digitalColorName' ||
    fieldKey === 'digitalColorHex' ||
    fieldKey === 'garmentColor' ||
    fieldKey === 'garmentFabric' ||
    fieldKey === 'outlineColor' ||
    fieldKey === 'backgroundFabricColor' ||
    fieldKey === 'patchColor' ||
    fieldKey === 'patchBorderColor' ||
    fieldKey === 'patchBorderThreadColor' ||
    fieldKey === 'patchBackgroundFabricColor') {
    return 'PRODUCT DETAILS';
  }

  if (lowerKey.includes('art') ||
    lowerKey.includes('dpi') ||
    lowerKey.includes('height') ||
    lowerKey.includes('width') ||
    lowerKey.includes('design') ||
    lowerKey.includes('upload') ||
    lowerKey.includes('filename') ||
    lowerKey.includes('aspect') ||
    lowerKey.includes('banner') ||
    lowerKey.includes('font') ||
    lowerKey.includes('vector') ||
    lowerKey.includes('raster') ||
    lowerKey.includes('legacy') ||
    lowerKey.includes('quantities')) {
    return 'ART DETAILS';
  }

  if (lowerKey.includes('color') ||
    lowerKey.includes('thread') ||
    lowerKey.includes('material') ||
    lowerKey.includes('pantone') ||
    lowerKey.includes('fabric') ||
    lowerKey.includes('pattern') ||
    lowerKey.includes('wei') ||
    lowerKey.includes('description')) {
    return 'MATERIAL SELECTION DETAILS';
  }

  return 'PRODUCT DETAILS';
};

const topFields = ['serviceType', 'productType'];
const splitButtonFields = ['splitFront', 'splitPosition', 'splitType', 'seamDistance', 'splitStyle', 'buttonPosition', 'buttonDistance'];
const mediaFields = ['media', 'outlineType', 'outlineColor', 'digitalColorHex', 'digitalColorName'];
const sewFileFields = ['sewFile', 'sewFileType', 'sewFileFormat', 'stitchType', 'sameStitchType', 'sewFileAppliqueStop', 'sewFileQuiltedGarmentFlag', 'sewFileAppliedToHat', 'colorAllStitchType'];

const reorderFieldsWithServiceAndProductType = (cardData: UnifiedData): UnifiedData => {
  const reorderedData: UnifiedData = {};
  const allGroupedFields = [...topFields, ...splitButtonFields, ...mediaFields, ...sewFileFields];

  topFields.forEach(field => {
    if (cardData[field] !== undefined) {
      reorderedData[field] = cardData[field];
    }
  });

  splitButtonFields.forEach(field => {
    if (cardData[field] !== undefined) {
      reorderedData[field] = cardData[field];
    }
  });

  mediaFields.forEach(field => {
    if (cardData[field] !== undefined) {
      reorderedData[field] = cardData[field];
    }
  });

  sewFileFields.forEach(field => {
    if (cardData[field] !== undefined) {
      reorderedData[field] = cardData[field];
    }
  });

  Object.entries(cardData).forEach(([key, value]) => {
    if (!allGroupedFields.includes(key)) {
      reorderedData[key] = value;
    }
  });

  return reorderedData;
};

const getFieldGroupInfo = (
  key: string,
  cardTitle: string,
  allKeys: string[],
  productDetailsKeys: string[]
): {
  groups: {
    name: string;
    fields: string[];
  }[],
  groupName: string | null,
  isGroupStart: boolean,
  isGroupEnd: boolean
} => {
  if (cardTitle !== 'PRODUCT DETAILS') {
    return { groups: [], groupName: null, isGroupStart: false, isGroupEnd: false };
  }

  const groups: { name: string, fields: string[] }[] = [
    {
      name: 'split-fields',
      fields: splitButtonFields
    },
    {
      name: 'media-fields',
      fields: mediaFields
    },
    {
      name: 'sew-file-fields',
      fields: sewFileFields
    }
  ];

  const filteredGroups = groups.filter(groupDef =>
    groupDef.fields.some(field => productDetailsKeys.includes(field))
  );

  const group = groups.find(g => g.fields.includes(key));
  if (!group) {
    return { groups: filteredGroups, groupName: null, isGroupStart: false, isGroupEnd: false };
  }

  const presentKeys = group.fields.filter(k => allKeys.includes(k));
  if (presentKeys.length === 0) {
    return { groups: filteredGroups, groupName: group.name, isGroupStart: false, isGroupEnd: false };
  }

  const lastGroupKey = presentKeys.reduce((lastKey, currentKey) => {
    const currentIndex = allKeys.indexOf(currentKey);
    const lastIndex = allKeys.indexOf(lastKey);
    return currentIndex > lastIndex ? currentKey : lastKey;
  }, presentKeys[0]);

  return {
    groups: filteredGroups,
    groupName: group.name,
    isGroupStart: key === presentKeys[0],
    isGroupEnd: key === lastGroupKey
  };
};

const reorderArtDetailsFields = (cardData: UnifiedData): UnifiedData => {
  const reorderedData: UnifiedData = {};

  if (cardData['artId'] !== undefined) {
    reorderedData['artId'] = cardData['artId'];
  }

  Object.entries(cardData).forEach(([key, value]) => {
    if (key !== 'artId') {
      reorderedData[key] = value;
    }
  });

  return reorderedData;
};

const applyProductRules = (
  unifiedData: UnifiedData,
  mapLabels: LabelMap | null,
  productType: string | null,
  cardConfigs: CardConfig[]
): { processedData: Map<string, UnifiedData>; cardConfigs: CardConfig[], rules: ProductRules } => {
  const processedData = new Map<string, UnifiedData>();
  const modifiedCardConfigs = JSON.parse(JSON.stringify(cardConfigs));

  modifiedCardConfigs.forEach((card: any) => {
    processedData.set(card.title, {});
  });

  const rules = productType && PRODUCT_FIELD_RULES[productType] ? PRODUCT_FIELD_RULES[productType] : {};

  Object.entries(unifiedData).forEach(([key, value]) => {
    const rule = rules[key];
    const isFromMapLabels = mapLabels && key in mapLabels;

    // hide based on rules
    if (rule?.hide) {
      return;
    }

    let targetCard = findFieldInCards(key, modifiedCardConfigs, isFromMapLabels as boolean | undefined);

    // moveTo rule
    if (rule?.moveTo) {
      targetCard = rule.moveTo;
    }

    // moveToTop rule
    if (rule?.moveToTop) {
      targetCard = rule.moveToTop;
    }

    const cardData = processedData.get(targetCard) || {};
    cardData[key] = value;
    processedData.set(targetCard, cardData);
  });

  return { processedData, cardConfigs: modifiedCardConfigs, rules };
};

const applyMoveRules = (
  processedData: Map<string, UnifiedData>,
  rules: any
): void => {
  const moveRules: { [key: string]: { type: 'moveAfter' | 'moveBefore'; targets: string[] } } = {};
  Object.entries(rules).forEach(([fieldKey, rule]: [string, any]) => {
    if (rule?.moveAfter) {
      const targets = Array.isArray(rule.moveAfter) ? rule.moveAfter : [rule.moveAfter];
      moveRules[fieldKey] = { type: 'moveAfter', targets };
    } else if (rule?.moveBefore) {
      const targets = Array.isArray(rule.moveBefore) ? rule.moveBefore : [rule.moveBefore];
      moveRules[fieldKey] = { type: 'moveBefore', targets };
    }
  });

  processedData.forEach((cardData, cardTitle) => {
    const entries = Object.entries(cardData);
    const reorderedData: UnifiedData = {};

    entries.forEach(([key, value]) => {
      if (!moveRules[key]) {
        reorderedData[key] = value;
      }
    });

    entries.forEach(([key, value]) => {
      if (moveRules[key]) {
        const { type, targets } = moveRules[key];
        const reorderedEntries = Object.entries(reorderedData);

        let targetIndex = -1;
        for (const target of targets) {
          targetIndex = reorderedEntries.findIndex(([k]) => k === target);
          if (targetIndex !== -1) break;
        }

        if (targetIndex !== -1) {
          const newData: UnifiedData = {};

          if (type === 'moveAfter') {
            for (let i = 0; i <= targetIndex; i++) {
              const [k, v] = reorderedEntries[i];
              newData[k] = v;
            }

            newData[key] = value;

            for (let i = targetIndex + 1; i < reorderedEntries.length; i++) {
              const [k, v] = reorderedEntries[i];
              newData[k] = v;
            }
          } else if (type === 'moveBefore') {
            for (let i = 0; i < targetIndex; i++) {
              const [k, v] = reorderedEntries[i];
              newData[k] = v;
            }

            newData[key] = value;

            for (let i = targetIndex; i < reorderedEntries.length; i++) {
              const [k, v] = reorderedEntries[i];
              newData[k] = v;
            }
          }

          Object.keys(reorderedData).forEach(k => delete reorderedData[k]);
          Object.assign(reorderedData, newData);
        } else {
          reorderedData[key] = value;
        }
      }
    });

    processedData.set(cardTitle, reorderedData);
  });
};

export const useArtDetails = (
  wizardData: any,
  wizardProps: any,
  isEasyView: boolean,
  isLNTransfer: boolean,
  artId: string | null
) => {
  const productType = wizardData?.productType ?? wizardData.serviceType; // (just for CustomProduct --> Uploaded File)
  const [labelMap, setLabelMap] = useState<Map<number, Label[]> | null>(null);
  const [labelKeys, setLabelKeys] = useState<Set<string>>(new Set());
  const [mapLabels, setMapLabels] = useState<LabelMap | null>(null);

  useEffect(() => {
    if (!wizardData) return;

    mapWizardData(wizardData).then(result => {
      setLabelMap(result.groupedLabels);
      setLabelKeys(new Set(Object.keys(result.mapLabels)));
      setMapLabels(result.mapLabels);
    });
  }, [wizardData]);

  const { mappedCards, mappedKeys, unmappedKeys } = useMemo(() => {
    if (!wizardProps) return { mappedCards: [], mappedKeys: new Set<string>(), unmappedKeys: [] };

    const mappedCards = mapDataToCards(isLNTransfer, isEasyView);
    const mappedKeys = new Set<string>(
      mappedCards.flatMap(cardConfig =>
        cardConfig.properties
          .map(property => String(property.key))
          .filter(key => !labelKeys.has(key))
      )
    );

    const allPropsKeys = Object.keys(wizardProps);
    const allExtendedKeys =
      isEasyView && wizardData?.extendedData
        ? Object.keys(wizardData.extendedData)
        : [];

    const adminId = getAdminId();
    const unmappedKeys = Array.from(
      new Set(
        allPropsKeys
          .filter(
            key =>
              key !== 'extendedData' &&
              key !== 'quote' &&
              !mappedKeys.has(key) &&
              !labelKeys.has(key) &&
              (adminId || !internalEmbroideryNoteKeys.includes(key))
          )
          .concat(
            allExtendedKeys.filter(
              key => !mappedKeys.has(key) && !labelKeys.has(key)
            )
          )
      )
    );

    return { mappedCards, mappedKeys, unmappedKeys };
  }, [wizardProps, wizardData, isEasyView, isLNTransfer, labelKeys]);

  const { processedDataByCards } = useMemo(() => {
    const getAutoFixDataReport = (autoFixData: any) => {
      const report: {[key:string]:any} = {};
      const autoFixDataChanges = (autoFixData.AppState.Final.ArtPrepReported.AppliedChanges as { Text: string }[]).map(x => x.Text)
      if (autoFixDataChanges.length > 0) {
        report.autoFixDataChanges = autoFixDataChanges.map(x => `<div>- ${x}<br/></div>`).join('');
      }
      const autoFixDataProblems = (autoFixData.AppState.Final.ArtPrepReported.ProblemAreas as { Text: string }[]).map(x => x.Text)
      if (autoFixDataProblems.length > 0) {
        report.autoFixDataProblems = autoFixDataProblems.map(x => `<div>- ${x}<br/></div>`).join('');
      }
      const autoFixDataWarnings = (autoFixData.AppState.Final.Warnings as { Warning: string }[]).map(x => x.Warning)
      if (autoFixDataWarnings.length > 0) {
        report.autoFixDataWarnings = autoFixDataWarnings.map(x => `<div>- ${x}<br/></div>`).join('');
      }
      if (autoFixData.WhiteBackgroundAnalysis.containsWhite) {
        report.autoFixRemoveWhiteOption = `<div>- ${autoFixData.RemoveWhite.selectedRasterOption}<br/></div>`;
      }
      if (autoFixData.SoftEdgesFixed) {
        report.autoFixSoftEdgesOption = `<div>- ${autoFixData.SoftEdgeAutoFix.selectedOption}<br/></div>`;
      }
      return report;
    }
    const hasAutoFixData = "autoFixData" in wizardData && !wizardData.autoFixData.ProofSelected;
    const autoFixDataReport = hasAutoFixData ? getAutoFixDataReport(wizardData.autoFixData) : {};
    const rawUnifiedData = createUnifiedData({...wizardData, ...autoFixDataReport}, wizardProps, mapLabels, mappedCards, artId);
    const { processedData, rules } = applyProductRules(
      rawUnifiedData,
      mapLabels,
      productType,
      mappedCards
    );

    applyMoveRules(processedData, rules);

    const reorderedData = new Map<string, UnifiedData>();
    processedData.forEach((cardData, cardTitle) => {
      if (cardTitle === 'PRODUCT DETAILS') {
        reorderedData.set(cardTitle, reorderFieldsWithServiceAndProductType(cardData));
      } else if (cardTitle === 'ART DETAILS') {
        reorderedData.set(cardTitle, reorderArtDetailsFields(cardData));
      } else {
        reorderedData.set(cardTitle, cardData);
      }
    });

    return {
      unifiedData: rawUnifiedData,
      processedDataByCards: reorderedData
    };
  }, [wizardData, wizardProps, mapLabels, artId]);

  return {
    labelMap,
    labelKeys,
    mappedCards,
    mappedKeys,
    unmappedKeys,
    processedDataByCards,
    getFieldGroupInfo
  };
}